/**
 * Settings → Connectors (item 65A): the connectors this person's
 * organization chose to have each person sign in to, on agents they can
 * use — Connect, Disconnect, "Connected as …", paused (and why), removed.
 *
 * People never add a connector here: there is no Add. And until a manager
 * chooses per-person sign-in for something this person uses, the section is
 * not there at all — nothing changes for anyone before that (G3).
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiOutlined } from '@ant-design/icons';
import { useLanguage } from '@hooks/useLanguage';
import { useFeature } from '@hooks/useFeature';
import { myConnectorsService, type MyConnector } from '../services/myConnectorsService';
import { PersonalConnect } from './PersonalConnect';

export function MyConnectorsSettings() {
  const { t } = useLanguage();
  const mcpOn = useFeature('integrations.mcp_connectors');
  const apiOn = useFeature('integrations.api_connectors');
  const [items, setItems] = useState<MyConnector[] | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await myConnectorsService.list());
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => { if (mcpOn || apiOn) void load(); }, [load, mcpOn, apiOn]);

  if (!(mcpOn || apiOn) || !items || items.length === 0) return null;
  return (
    <div className="settings-section" data-testid="my-connectors">
      <h2 className="section-title">
        <ApiOutlined style={{ marginInlineEnd: 8 }} />
        {t('myConnectors.title')}
      </h2>
      <p className="settings-subtitle" style={{ marginBottom: 16 }}>{t('myConnectors.subtitle')}</p>
      <div className="pcx-list">
        {items.map(c => <PersonalConnect key={c.entry_id} connector={c} onChanged={() => { void load(); }} />)}
      </div>
    </div>
  );
}
